// Monthly payment and closing cost math, shared by Tools > Monthly Payment Calculator / Closing Cost
// Estimator and the MCP mortgage_calculator / closing_cost_estimator tools. CommonJS, no imports
// except the shared Florida title rate.
const { floridaOwnersTitlePremium } = require('./netSheet');
const num = (v) => { const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.-]/g, '')); return Number.isFinite(n) ? n : 0; };

const MORTGAGE_DEFAULTS = { price: '', downPct: '20', rate: '', years: '30', tax: '', insurance: '2500', hoa: '', hoaPeriod: 'month', cdd: '', pmiPct: '0.5' };
// v: form values; extras: [{ amount }] monthly.
function mortgagePayment(v, extras = []) {
  const price = num(v.price); const down = num(v.downPct);
  const loan = Math.max(price * (1 - down / 100), 0);
  const r = num(v.rate) / 100 / 12;
  const n = Math.max(num(v.years) * 12, 1);
  const pi = r === 0 ? loan / n : loan * r / (1 - Math.pow(1 + r, -n));
  const taxMo = num(v.tax) / 12; const insMo = num(v.insurance) / 12;
  const hoaMo = v.hoaPeriod === 'quarter' ? num(v.hoa) / 3 : num(v.hoa);
  const cddMo = num(v.cdd) / 12;
  const pmiMo = down < 20 ? loan * (num(v.pmiPct) / 100) / 12 : 0;
  const extrasMo = (extras || []).reduce((s, e) => s + num(e.amount), 0);
  const total = pi + taxMo + insMo + hoaMo + cddMo + pmiMo + extrasMo;
  return { price, down, loan, r, n, pi, taxMo, insMo, hoaMo, cddMo, pmiMo, extrasMo, total, totalInterest: pi * n - loan, cashToClose: price * (down / 100), rateMissing: String(v.rate == null ? '' : v.rate).trim() === '' };
}

// Florida rates set by law.
const DEED_DOC_STAMP_RATE = 0.007; const MORTGAGE_DOC_STAMP_RATE = 0.0035; const INTANGIBLE_TAX_RATE = 0.002;
const CLOSING_DEFAULTS = {
  state: 'FL', ownerTitlePayer: 'seller', stateTax: '0', price: '600000', downPct: '20',
  lenderTitle: '1200', settlement: '1500', recording: '150', lenderFees: '3500', appraisal: '600', prepaids: '3000',
  commissionPct: '3', sellerSettlement: '1000', sellerRecording: '300', survey: '400', payoff: '0',
};
function closingCosts(v) {
  const salePrice = num(v.price); const loan = salePrice * (1 - num(v.downPct) / 100); const down = salePrice - loan;
  const isFL = (v.state || 'FL') === 'FL';
  const title = floridaOwnersTitlePremium(salePrice);
  const buyerLines = [
    ...(isFL ? [{ label: 'Mortgage doc stamps (0.35% of loan)', amount: loan * MORTGAGE_DOC_STAMP_RATE }, { label: 'Intangible tax on mortgage (0.2% of loan)', amount: loan * INTANGIBLE_TAX_RATE }]
      : [{ label: 'State mortgage taxes (enter manually)', amount: num(v.stateTax) }]),
    { label: "Lender's title insurance (estimate)", amount: num(v.lenderTitle) },
    ...(v.ownerTitlePayer === 'buyer' ? [{ label: 'Owner\u2019s title insurance (estimate)', amount: title }] : []),
    { label: 'Title and settlement fees (estimate)', amount: num(v.settlement) },
    { label: 'Recording fees (estimate)', amount: num(v.recording) },
    { label: 'Lender fees (estimate)', amount: num(v.lenderFees) },
    { label: 'Appraisal (estimate)', amount: num(v.appraisal) },
    { label: 'Prepaid taxes and insurance (estimate)', amount: num(v.prepaids) },
  ];
  const buyerClosing = buyerLines.reduce((s, l) => s + l.amount, 0);
  const commission = salePrice * (num(v.commissionPct) / 100);
  const sellerLines = [
    ...(isFL ? [{ label: 'Deed doc stamps (0.70% of sale price)', amount: salePrice * DEED_DOC_STAMP_RATE }] : [{ label: 'State transfer taxes (enter manually)', amount: num(v.stateTax) }]),
    ...(v.ownerTitlePayer === 'seller' ? [{ label: 'Owner\u2019s title insurance (estimate)', amount: title }] : []),
    { label: 'Title and settlement fees (estimate)', amount: num(v.sellerSettlement) },
    { label: 'Recording and release fees (estimate)', amount: num(v.sellerRecording) },
    { label: 'Survey and HOA documents (estimate)', amount: num(v.survey) },
  ];
  const sellerCosts = sellerLines.reduce((s, l) => s + l.amount, 0);
  return { salePrice, loan, down, buyerLines, buyerClosing, buyerCashToClose: down + buyerClosing, commission, sellerLines, sellerCosts, sellerNet: salePrice - commission - sellerCosts - num(v.payoff) };
}
module.exports = { MORTGAGE_DEFAULTS, mortgagePayment, CLOSING_DEFAULTS, closingCosts, DEED_DOC_STAMP_RATE, MORTGAGE_DOC_STAMP_RATE, INTANGIBLE_TAX_RATE };
